<template>
  <div class="px-24 pb-16 pt-16 md:pt-8">
    <div class="text-static-default-mid body-2 mb-16">
      {{ i18n(translations.description) }}
    </div>

    <RevInputText
      id="country-switch-search-country"
      v-model="countryLookup"
      :aria-activedescendant="i18n.country(thisMarket.countryCode)"
      aria-autocomplete="list"
      aria-controls="country-switch-countries-list"
      aria-expanded="true"
      autocomplete="off"
      :icon="IconSearch"
      :label="i18n(translations.searchCountryLabel)"
      role="combobox"
      type="text"
      @clear="() => (countryLookup = '')"
    />
  </div>

  <RevList
    id="country-switch-countries-list"
    class="body-2-bold divide-y-0 overflow-auto border-y-0 md:max-h-[40vh]"
    role="listbox"
    :variant
  >
    <div v-if="!countryLookup" class="mt-12 md:mt-0">
      <p class="body-1-bold px-24 lg:pb-4">
        <span v-if="enableEsUsContent">
          {{ i18n(translations.currentLocationTitleMultiLocale) }}
        </span>
        <span v-else>{{ i18n(translations.currentLocationTitle) }}</span>
      </p>

      <CountrySwitchMenuListItem
        :key="thisMarket.countryCode"
        :countryCode="thisMarket.countryCode"
        :defaultLocale="thisMarket.defaultLocale"
        :locales="thisMarket.locales"
      />

      <div class="px-24 py-12">
        <RevDivider class="grow" />
      </div>

      <div v-if="!enableEsUsContent" class="px-24 pb-4 pt-8">
        {{ i18n(translations.currentAllMarketsTitle) }}
      </div>
    </div>

    <CountrySwitchMenuListItem
      v-for="market in filteredMarkets"
      :key="market.countryCode"
      :countryCode="market.countryCode"
      :defaultLocale="market.defaultLocale"
      :locales="market.locales"
    />
  </RevList>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { RevDivider } from '@ds/components/Divider'
import { RevInputText } from '@ds/components/InputText'
import { RevList } from '@ds/components/List'
import type { ListVariant } from '@ds/components/List/List.constant'
import { IconSearch } from '@ds/icons/IconSearch'

import { countryDisplayNameLocale } from '../../utils/countryDisplayName'

import translations from './CountrySwitch.translations'
import CountrySwitchMenuListItem from './CountrySwitchMenuListItem.vue'

withDefaults(
  defineProps<{
    variant?: ListVariant
  }>(),
  { variant: 'default' },
)

const i18n = useI18n()
const thisMarket = useMarketplace().market
const { allMarkets } = useMarketplace()
const countryLookup = ref('')

const experiments = useExperiments()
const enableEsUsContent =
  experiments['experiment.enableEsUsContent'] === 'enabled'

const filteredMarkets = computed(() => {
  const input = countryLookup.value.toLowerCase()
  if (!input) return Object.values(allMarkets)

  return Object.values(allMarkets).filter(
    (market) =>
      i18n.country(market.countryCode).toLowerCase().includes(input) ||
      countryDisplayNameLocale(market.defaultLocale, market.countryCode)
        .toLowerCase()
        .includes(input),
  )
})
</script>
